import { CoursePageFooter } from '@/components/course/CoursePageFooter';
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader';
import { CoursesCatalogCard } from '@/components/courses/CoursesCatalogCard';
import { CoursesCatalogRow } from '@/components/courses/CoursesCatalogRow';
import { HUB_COPY } from '@/components/courses/personalHubTokens';
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon';
import { BodyText, CaptionText, ErrorDisplay, PageTitle } from '@/components/ui';
import { DashboardViewModeToggle } from '@/components/ui/DashboardViewModeToggle';
import { purchasedCoursesCategoryQueryOptions } from '@/hooks/purchasedCoursesQueries';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePersistedState } from '@/hooks/usePersistedState';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS } from '@/lib/brandColors';
import { courseGridLayout } from '@/lib/courseGridLayout';
import { DASHBOARD_GRID_GAP } from '@/lib/dashboardToolbarUi';
import { chunkIntoRows } from '@/lib/gridRows';
import { purchasedCourseHref } from '@/lib/purchasedCourseHref';
import {
  CONTENT_MAX_WIDTH,
  useAutoHideScrollbar,
  useCatalogProgressBarHeight,
  useIsMobile,
  useStableWindowWidth,
} from '@/lib/useDeviceType';
import type { Course, CourseThumbnailItem } from '@/types';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { FlatList, Platform, type ListRenderItem } from 'react-native';
import { XStack, YStack } from 'tamagui';

const CONTAINER_PAD_X = 16;
const GRID_GAP = DASHBOARD_GRID_GAP;
const PAGE_TITLE = 'Purchased Courses';

const RowSeparator = () => <YStack h={GRID_GAP} />;

/** One card/row on the page. Companion (per-lesson) grants share a course id,
 * so the key also carries the lesson slug. */
type PurchasedEntry = {
  key: string;
  course: Course;
  href: string;
  thumbnailUri: string | null;
  progressPercentage: number;
};

function toEntry(item: CourseThumbnailItem): PurchasedEntry {
  return {
    key: `${item.id}-${item.lessonSlug ?? 'course'}`,
    course: {
      id: item.id,
      title: item.title ?? '',
      slug: item.slug,
      status: item.status,
      thumbnail: item.thumbnail ?? null,
      summary: item.summary ?? null,
      skill_level: item.skillLevel ?? null,
      instructor_names: item.instructorNames ?? null,
    },
    href: purchasedCourseHref(item),
    thumbnailUri: item.thumbnail ?? null,
    progressPercentage: item.progressPercentage ?? 0,
  };
}

export default function PurchasedCoursesAllScreen() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const stableWidth = useStableWindowWidth();
  const progressBarHeight = useCatalogProgressBarHeight();
  const { scrollbarStyle, onScroll } = useAutoHideScrollbar();

  useDocumentTitle(PAGE_TITLE);

  // Same storage pattern as the courses hub; lists are easiest to scan for a
  // large library, so this page defaults to list.
  const [viewMode, setViewMode, viewModeLoaded] = usePersistedState<'grid' | 'list'>(
    'purchased_courses_view_mode',
    'list',
  );

  const {
    data: purchasedCategory,
    isLoading,
    error,
    refetch,
  } = useQuery(purchasedCoursesCategoryQueryOptions(user?.id ?? null));

  const entries = useMemo(
    () => (purchasedCategory?.courses ?? []).map(toEntry),
    [purchasedCategory],
  );

  const pageWidth = Math.min(CONTENT_MAX_WIDTH, stableWidth);
  const gridInnerWidth = pageWidth - CONTAINER_PAD_X * 2;
  const layout = useMemo(() => courseGridLayout(gridInnerWidth), [gridInnerWidth]);
  const { cardWidth, numColumns: gridColumns, justify } = layout;

  // Pre-chunked rows keep the FlatList single-column across grid/list so a
  // toggle never remounts the list (see courses hub).
  const rows = useMemo<PurchasedEntry[][]>(
    () => chunkIntoRows(entries, viewMode === 'list' ? 1 : gridColumns),
    [entries, viewMode, gridColumns],
  );

  const renderItem: ListRenderItem<PurchasedEntry[]> = useCallback(
    ({ item: row }) => {
      if (viewMode === 'list') {
        const entry = row[0];
        return (
          <YStack
            width={pageWidth}
            maxWidth={CONTENT_MAX_WIDTH}
            px={CONTAINER_PAD_X}
            als="center"
          >
            <CoursesCatalogRow
              course={entry.course}
              href={entry.href}
              thumbnailUri={entry.thumbnailUri}
              progressPercentage={entry.progressPercentage}
              progressBarHeight={progressBarHeight}
              isMobile={isMobile}
            />
          </YStack>
        );
      }
      return (
        <XStack
          width={gridInnerWidth}
          maxWidth={CONTENT_MAX_WIDTH - CONTAINER_PAD_X * 2}
          als="center"
          gap={GRID_GAP}
          jc={justify === 'center' ? 'center' : 'flex-start'}
        >
          {row.map((entry) => (
            <YStack key={entry.key} w={cardWidth} style={{ flexGrow: 0, flexShrink: 0 }}>
              <CoursesCatalogCard
                course={entry.course}
                href={entry.href}
                width={cardWidth}
                thumbnailUri={entry.thumbnailUri}
                progressPercentage={entry.progressPercentage}
                progressBarHeight={progressBarHeight}
                isMobile={isMobile}
              />
            </YStack>
          ))}
        </XStack>
      );
    },
    [
      viewMode,
      pageWidth,
      gridInnerWidth,
      justify,
      cardWidth,
      progressBarHeight,
      isMobile,
    ],
  );

  const keyExtractor = useCallback(
    (row: PurchasedEntry[], index: number) =>
      `${viewMode}-${row[0]?.key ?? 'row'}-${index}`,
    [viewMode],
  );

  const listHeader = useMemo(
    () => (
      <YStack pt="$5" pb="$3" width={pageWidth} maxWidth={CONTENT_MAX_WIDTH} px={CONTAINER_PAD_X}>
        <XStack ai="center" jc="space-between" flexWrap="wrap" gap="$2">
          <XStack ai="baseline" gap="$2" flexShrink={1} minWidth={0}>
            <PageTitle tag="h1" fontWeight="700" color="$color">
              {PAGE_TITLE}
            </PageTitle>
            {entries.length > 0 ? (
              <CaptionText color={BRAND_COLORS.muted}>
                {HUB_COPY.purchased.countLabel(entries.length)}
              </CaptionText>
            ) : null}
          </XStack>
          {entries.length > 0 ? (
            <DashboardViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              gap="$2"
            />
          ) : null}
        </XStack>

        {entries.length === 0 ? (
          <YStack mt="$3" bg="#F4F1ED" borderRadius="$4" px="$4" py="$3">
            <BodyText color="$color" textAlign="center">
              You have not yet purchased courses with lifetime access
            </BodyText>
          </YStack>
        ) : null}
      </YStack>
    ),
    [pageWidth, entries.length, viewMode, setViewMode],
  );

  if (isLoading || !viewModeLoaded) {
    return (
      <YStack f={1} ai="center" jc="center" bg="$bg" p="$4">
        <HelpScoutBeacon />
        <CoursePagePremiumLoader variant="coursesList" />
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack f={1} bg="#F8F9FA">
        <ErrorDisplay error={error as Error} onRetry={() => void refetch()} />
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg">
      <HelpScoutBeacon />
      <FlatList
        style={{ flex: 1, width: '100%', ...scrollbarStyle }}
        data={rows}
        keyExtractor={keyExtractor}
        ListHeaderComponent={listHeader}
        ListFooterComponent={<CoursePageFooter />}
        renderItem={renderItem}
        ItemSeparatorComponent={RowSeparator}
        contentContainerStyle={{
          alignItems: 'center',
          paddingBottom: 24,
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        onScroll={onScroll}
        scrollEventThrottle={16}
        removeClippedSubviews={Platform.OS !== 'web'}
        initialNumToRender={8}
        maxToRenderPerBatch={8}
        windowSize={5}
      />
    </YStack>
  );
}
